import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

export function ProtectedRoute({ children, adminOnly = false }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    async function checkAuthAndAdmin() {
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        const authenticated = !!session;
        setIsAuthenticated(authenticated);

        // If admin-only, check if user has admin role
        if (authenticated && adminOnly) {
          const { data: userData, error } = await supabase
            .from('users')
            .select('is_admin')
            .eq('user_id', session.user.id)
            .single();

          if (!error && userData) {
            setIsAdmin(!!userData.is_admin);
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setLoading(false);
      }
    }

    checkAuthAndAdmin();
  }, [adminOnly]);

  if (loading) {
    // Show loading state
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-brand-secondary">Loading...</p>
      </div>
    );
  }

  // For admin-only routes, check both authentication and admin status
  if (adminOnly) {
    return isAuthenticated && isAdmin ? (
      <>{children}</>
    ) : (
      <Navigate to="/" replace />
    );
  }

  // For regular protected routes, just check authentication
  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
} 